import React, { useState, useEffect } from 'react';
import { userService, User } from '../services/api';
import { useTranslation } from 'react-i18next';
import { Trash2, Search, User as UserIcon, Shield, ShieldCheck, CheckCircle } from 'lucide-react';

const ManageUsersPage: React.FC = () => {
    const { t } = useTranslation();
    const [users, setUsers] = useState<User[]>([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [currentUser, setCurrentUser] = useState<User | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [editingUser, setEditingUser] = useState<User | null>(null);
    const [editPoints, setEditPoints] = useState<number>(0);
    const [editIsBanned, setEditIsBanned] = useState<boolean>(false);
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);

    // Debounce search
    const [debouncedSearch, setDebouncedSearch] = useState('');

    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedSearch(searchTerm);
        }, 500);
        return () => clearTimeout(timer);
    }, [searchTerm]);

    useEffect(() => {
        // Get current user from local storage
        const userStr = localStorage.getItem('user');
        if (userStr) {
            setCurrentUser(JSON.parse(userStr));
        }
    }, []);

    useEffect(() => {
        fetchUsers();
    }, [page, debouncedSearch]);

    const fetchUsers = async () => {
        try {
            setLoading(true);
            setError(null);
            const response = await userService.getUsers({
                page,
                limit: 10,
                search: debouncedSearch,
            });
            setUsers(response.data.data.users);
            setTotalPages(response.data.data.pagination.totalPages);
        } catch (error: any) {
            console.error('Error fetching users:', error);
            setError(error.response?.data?.message || error.message || 'Failed to load users');
        } finally {
            setLoading(false);
        }
    };

    const handleRoleChange = async (userId: number, newRole: string) => {
        try {
            await userService.updateUserRole(userId, newRole);
            setUsers(users.map(u => (u.id === userId ? { ...u, role: newRole as any } : u)));
            alert('User role updated successfully');
        } catch (error: any) {
            alert(error.response?.data?.message || 'Failed to update role');
            // Revert change in UI if needed, but since we map optimistically or fetch again, 
            // simple toast error is enough. Ideally, we refetch or handle state revert.
            fetchUsers();
        }
    };

    const handleDeleteUser = async (userId: number) => {
        if (!window.confirm('Are you sure you want to delete this user? This action cannot be undone.')) {
            return;
        }

        try {
            await userService.deleteUser(userId);
            setUsers(users.filter(u => u.id !== userId));
            alert('User deleted successfully');
        } catch (error: any) {
            alert(error.response?.data?.message || 'Failed to delete user');
        }
    };

    const handleEditDetails = (user: User) => {
        setEditingUser(user);
        setEditPoints(user.points ?? 0);
        setEditIsBanned(user.isBannedFromReporting || false);
        setIsEditModalOpen(true);
    };

    const handleSaveUserDetails = async () => {
        if (!editingUser) return;

        try {
            await userService.updateUserDetails(editingUser.id, {
                points: editPoints,
                isBannedFromReporting: editIsBanned
            });

            setUsers(users.map(u => u.id === editingUser.id ? {
                ...u,
                points: editPoints,
                isBannedFromReporting: editIsBanned
            } : u));

            setIsEditModalOpen(false);
            setEditingUser(null);
            alert('User details updated successfully');
        } catch (error: any) {
            alert(error.response?.data?.message || 'Failed to update user details');
        }
    };

    // Helper to check if current user can modify target user
    const canModify = (targetUser: User) => {
        if (!currentUser) return false;
        if (currentUser.id === targetUser.id) return false; // Cannot modify self (usually) or risky for role

        if (currentUser.role === 'admin') return true;
        if (currentUser.role === 'moderator') {
            return targetUser.role !== 'admin';
        }
        return false;
    };

    const canDelete = () => {
        return currentUser?.role === 'admin';
    };

    const getRoleIcon = (role: string) => {
        switch (role) {
            case 'admin':
                return <ShieldCheck className="w-4 h-4 text-red-600" />;
            case 'moderator':
                return <Shield className="w-4 h-4 text-blue-600" />;
            case 'approved_user':
                return <CheckCircle className="w-4 h-4 text-green-600" />;
            case 'advanced_user':
                return <ShieldCheck className="w-4 h-4 text-purple-600" />;
            default:
                return <UserIcon className="w-4 h-4 text-gray-400" />;
        }
    };

    return (
        <div className="container mx-auto px-4 py-8">
            <div className="flex justify-between items-center mb-6">
                <h1 className="text-2xl font-bold text-gray-900">{t('manageUsers.title')}</h1>
            </div>

            {/* Search */}
            <div className="mb-6 relative max-w-md">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Search className="h-5 w-5 text-gray-400" />
                </div>
                <input
                    type="text"
                    className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 sm:text-sm"
                    placeholder={t('manageUsers.searchPlaceholder')}
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                />
            </div>

            {/* Error Message */}
            {error && (
                <div className="mb-4 bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative" role="alert">
                    <strong className="font-bold">Error: </strong>
                    <span className="block sm:inline">{error}</span>
                </div>
            )}

            {/* Users Table */}
            <div className="bg-white shadow-sm rounded-lg overflow-hidden border border-gray-200">
                <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                            <tr>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('manageUsers.user')}</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('manageUsers.role')}</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('manageUsers.points')}</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('manageUsers.status')}</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('manageUsers.reportingStatus')}</th>
                                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">{t('manageUsers.actions')}</th>
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                            {loading ? (
                                <tr>
                                    <td colSpan={4} className="px-6 py-10 text-center text-gray-500">
                                        <div className="flex justify-center items-center">
                                            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                                            <span className="ml-2">{t('common.loading')}</span>
                                        </div>
                                    </td>
                                </tr>
                            ) : users.length === 0 ? (
                                <tr>
                                    <td colSpan={4} className="px-6 py-10 text-center text-gray-500">
                                        {t('manageUsers.noUsers')}
                                    </td>
                                </tr>
                            ) : (
                                users.map((user) => (
                                    <tr key={user.id} className="hover:bg-gray-50 transition-colors">
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <div className="flex items-center">
                                                <div className="flex-shrink-0 h-10 w-10">
                                                    {user.profilePicture ? (
                                                        <img className="h-10 w-10 rounded-full object-cover" src={user.profilePicture} alt="" />
                                                    ) : (
                                                        <div className="h-10 w-10 rounded-full bg-gray-200 flex items-center justify-center">
                                                            <span className="text-gray-500 font-medium text-sm">
                                                                {user.displayName?.charAt(0).toUpperCase() || user.firstName?.charAt(0).toUpperCase() || '?'}
                                                            </span>
                                                        </div>
                                                    )}
                                                </div>
                                                <div className="ml-4">
                                                    <div className="text-sm font-medium text-gray-900 flex items-center gap-1">
                                                        {user.displayName || `${user.firstName} ${user.lastName}`}
                                                        {user.id === currentUser?.id && <span className="text-xs bg-blue-100 text-blue-800 px-2 py-0.5 rounded-full ml-2">{t('manageUsers.you')}</span>}
                                                    </div>
                                                    <div className="text-sm text-gray-500">{user.email || 'No email'}</div>
                                                </div>
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <div className="flex items-center gap-2">
                                                {getRoleIcon(user.role)}
                                                {canModify(user) ? (
                                                    <select
                                                        value={user.role}
                                                        onChange={(e) => handleRoleChange(user.id, e.target.value)}
                                                        className={`text-sm border-none rounded-md focus:ring-2 bg-transparent cursor-pointer font-medium
                                    ${user.role === 'admin' ? 'text-red-600 focus:ring-red-500' :
                                                                user.role === 'moderator' ? 'text-blue-600 focus:ring-blue-500' : 'text-gray-700 focus:ring-gray-500'}`}
                                                    >
                                                        <option value="user">{t('manageUsers.roles.user')}</option>
                                                        <option value="approved_user">{t('manageUsers.roles.approved_user')}</option>
                                                        <option value="advanced_user">{t('manageUsers.roles.advanced_user')}</option>
                                                        <option value="moderator">{t('manageUsers.roles.moderator')}</option>
                                                        {/* Only show Admin option if current user is Admin */}
                                                        {currentUser?.role === 'admin' && <option value="admin">{t('manageUsers.roles.admin')}</option>}
                                                    </select>
                                                ) : (
                                                    <span className={`text-sm font-medium
                                ${user.role === 'admin' ? 'text-red-600' :
                                                            user.role === 'moderator' ? 'text-blue-600' : 'text-gray-700'}`}>
                                                        {t(`manageUsers.roles.${user.role}`, { defaultValue: user.role })}
                                                    </span>
                                                )}
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                            {user.points}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${user.isActive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                                                {user.isActive ? t('manageUsers.statusActive') : t('manageUsers.statusInactive')}
                                            </span>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${user.isBannedFromReporting ? 'bg-red-100 text-red-800' : 'bg-green-100 text-green-800'}`}>
                                                {user.isBannedFromReporting ? 'Banned' : 'Allowed'}
                                            </span>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                            {/* Edit Details Button */}
                                            {(currentUser?.role === 'admin' || currentUser?.role === 'moderator') && (
                                                <button
                                                    onClick={() => handleEditDetails(user)}
                                                    className="text-blue-600 hover:text-blue-900 transition-colors p-1 rounded hover:bg-blue-50 mr-2"
                                                    title={t('manageUsers.editDetails')}
                                                >
                                                    <UserIcon className="w-5 h-5" />
                                                </button>
                                            )}
                                            {canDelete() && user.id !== currentUser?.id && (
                                                <button
                                                    onClick={() => handleDeleteUser(user.id)}
                                                    className="text-red-600 hover:text-red-900 transition-colors p-1 rounded hover:bg-red-50"
                                                    title={t('manageUsers.deleteUser')}
                                                >
                                                    <Trash2 className="w-5 h-5" />
                                                </button>
                                            )}
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>

                {/* Pagination */}
                {users.length > 0 && (
                    <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
                        <div className="flex-1 flex flex-col sm:flex-row items-center justify-between gap-4 sm:gap-0">
                            <div>
                                <p className="text-sm text-gray-700">
                                    {t('common.showingPage')} <span className="font-medium">{page}</span> {t('common.of')} <span className="font-medium">{totalPages}</span>
                                </p>
                            </div>
                            <div>
                                <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                                    <button
                                        onClick={() => setPage(p => Math.max(1, p - 1))}
                                        disabled={page === 1}
                                        className="relative inline-flex items-center px-4 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                        {t('common.previous')}
                                    </button>
                                    <button
                                        onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                                        disabled={page === totalPages}
                                        className="relative inline-flex items-center px-4 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                        {t('common.next')}
                                    </button>
                                </nav>
                            </div>
                        </div>
                    </div>
                )}
            </div>

            {/* Edit User Modal */}
            {isEditModalOpen && (
                <div className="fixed inset-0 z-50 overflow-y-auto">
                    <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
                        <div className="fixed inset-0 transition-opacity" aria-hidden="true">
                            <div className="absolute inset-0 bg-gray-500 opacity-75" onClick={() => setIsEditModalOpen(false)}></div>
                        </div>

                        <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>

                        <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
                            <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                                <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">
                                    Edit User Details: {editingUser?.displayName}
                                </h3>

                                <div className="mb-4">
                                    <label className="block text-gray-700 text-sm font-bold mb-2">
                                        Points
                                    </label>
                                    <input
                                        type="number"
                                        className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                                        value={editPoints}
                                        onChange={(e) => setEditPoints(Number(e.target.value))}
                                    />
                                </div>

                                <div className="mb-4">
                                    <label className="flex items-center space-x-3">
                                        <input
                                            type="checkbox"
                                            className="form-checkbox h-5 w-5 text-blue-600"
                                            checked={editIsBanned}
                                            onChange={(e) => setEditIsBanned(e.target.checked)}
                                        />
                                        <span className="text-gray-700 font-bold">Ban from Reporting</span>
                                    </label>
                                    <p className="text-sm text-gray-500 mt-1">
                                        If banned, user cannot submit new price reports.
                                    </p>
                                </div>
                            </div>
                            <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                                <button
                                    type="button"
                                    className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-blue-600 text-base font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:ml-3 sm:w-auto sm:text-sm"
                                    onClick={handleSaveUserDetails}
                                >
                                    Save Changes
                                </button>
                                <button
                                    type="button"
                                    className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                                    onClick={() => setIsEditModalOpen(false)}
                                >
                                    Cancel
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ManageUsersPage;
